import React, { useRef } from "react";
import { clsx } from "clsx";
import { type SkylightPlayer, VerticalVideoPlayer } from "@bloomberg/skylight";

import type { LineupVideoPlayerProps } from "~components/shared/video-player/VideoPlayerProvider";
import { TranslatedMessage } from "~i18n/translate";
import type { Image, Video } from "~types/types";
import sizeImage, { DEFAULT_IMAGE } from "~utils/sizeImage";

import styles from "./styles.module.css";

const COLOR_ENV = process.env.NEXT_PUBLIC_COLOR_ENV;

enum SkylightLogLevel {
    debug = "debug",
    warn = "warn",
    error = "error",
}

const getSkylightLogLevel = (): SkylightLogLevel => {
    switch (COLOR_ENV) {
        case "production":
            return SkylightLogLevel.error;
        case "development":
            return SkylightLogLevel.debug;
        default:
            // non-production
            return SkylightLogLevel.warn;
    }
};

export interface LineupVideoVodPlayerProps extends LineupVideoPlayerProps {
    video: Video;
    image?: Image;
    showCaption?: boolean;
    credit?: string;
    shareUrl?: string;
    isVerticalVideo?: boolean;
    hideInitialTitle?: boolean;
}

/**
 * @internal
 * exporting for tests. If this is reused elsewhere, it should be extracted to its own util file
 */
export function hideInitialTitleOnReady(title: string | undefined) {
    return (player: SkylightPlayer) => {
        // removes the player's title bar when the player first loads
        player.titleBar?.update({
            title: "",
        });

        // adds a handler that only fires once upon the first time the player plays
        // sets the title bar back after first play
        player.one("play", () =>
            player.titleBar?.update({
                title: title ?? "",
            })
        );
    };
}

export const LineupVideoVodPlayerSkylight = (
    props: LineupVideoVodPlayerProps
) => {
    const {
        image,
        video,
        showCaption = true,
        credit,
        shareUrl,
        isVerticalVideo = false,
        hideInitialTitle = false,
    } = props;

    const { title, bmmrId, thumbnail } = video;

    const poster = sizeImage(
        image?.baseUrl ?? thumbnail?.baseUrl ?? DEFAULT_IMAGE,
        640
    );

    const playerRef = useRef<SkylightPlayer>(null);

    const caption =
        (credit ? <p className={styles.caption}> {credit} </p> : "") ||
        (title && !isVerticalVideo && title.trim() !== "" ? (
            <p className={styles.caption}>
                <TranslatedMessage
                    id="watch"
                    defaultMessage="<b>Watch:</b> {title}"
                    values={{
                        title,
                        b: (chunks: React.ReactNode) => (
                            <span className={styles.boldPrepend}>{chunks}</span>
                        ),
                    }}
                />
            </p>
        ) : (
            ""
        ));

    const logLevel = getSkylightLogLevel();

    const onReady = hideInitialTitle
        ? hideInitialTitleOnReady(title)
        : undefined;

    return (
        <div
            className={clsx(
                styles.LineUpVideoPlayer,
                styles.SkylightVideoPlayer
            )}
            data-component="lineup-video-player-skylight"
        >
            <div
                className={
                    isVerticalVideo
                        ? styles.verticalVideoPlayerContainer
                        : styles.videoPlayerContainer
                }
                data-poster={poster}
            >
                <VerticalVideoPlayer
                    assetId={bmmrId ?? ""}
                    metadata={{
                        title,
                        canonicalUrl: shareUrl,
                    }}
                    options={{ poster }}
                    className={styles.videoPlayer}
                    logLevel={logLevel}
                    ref={playerRef}
                    onReady={onReady || undefined}
                    skylightOptions={{
                        npawTrackingMode:
                            COLOR_ENV === "production"
                                ? "production"
                                : "development",
                    }}
                />
            </div>
            {showCaption && caption}
        </div>
    );
};
